import { Type, type Static } from "@sinclair/typebox";
import { ExternalAccountBindingSchema } from "./external-account.ts";
import { UuidSchema } from "./identifiers.ts";

export const PRODUCT_AUTHORIZATION_SCHEMA_VERSION = "1.0" as const;
const TimestampSchema = Type.String({ format: "date-time" });

export const GlobalProductRoleSchema = Type.Union([
  Type.Literal("super_administrator"),
  Type.Literal("platform_operator")
]);
export type GlobalProductRole = Static<typeof GlobalProductRoleSchema>;

export const ProductRoleSchema = GlobalProductRoleSchema;
export type ProductRole = Static<typeof ProductRoleSchema>;

export const PermissionAreaSchema = Type.Union([
  Type.Literal("account"),
  Type.Literal("platform"),
  Type.Literal("project"),
  Type.Literal("content"),
  Type.Literal("knowledge")
]);
export type PermissionArea = Static<typeof PermissionAreaSchema>;

export const ProductActionSchema = Type.Union([
  Type.Literal("account.manage"),
  Type.Literal("platform.manage"),
  Type.Literal("project.create"),
  Type.Literal("project.manage"),
  Type.Literal("file.workspace.use"),
  Type.Literal("file.permission.manage"),
  Type.Literal("document.tag.manage"),
  Type.Literal("collection.manage"),
  Type.Literal("view.manage"),
  Type.Literal("processing.request"),
  Type.Literal("knowledge.query"),
  Type.Literal("knowledge.manage")
]);
export type ProductAction = Static<typeof ProductActionSchema>;

export const AUTHENTICATED_HUMAN_ACTIONS = Object.freeze([
  "project.create",
  "knowledge.manage"
] as const satisfies readonly ProductAction[]);

export interface ProductRoleDefinition {
  role: ProductRole;
  target: "global";
  permissionAreas: readonly PermissionArea[];
  actions: readonly ProductAction[];
}

export const PRODUCT_ROLE_CATALOG = Object.freeze({
  super_administrator: Object.freeze({
    role: "super_administrator",
    target: "global",
    permissionAreas: ["account", "platform", "project", "content", "knowledge"] as const,
    actions: [
      "account.manage",
      "platform.manage",
      "project.create",
      "project.manage",
      "file.workspace.use",
      "file.permission.manage",
      "document.tag.manage",
      "collection.manage",
      "view.manage",
      "processing.request",
      "knowledge.query",
      "knowledge.manage"
    ] as const
  }),
  platform_operator: Object.freeze({
    role: "platform_operator",
    target: "global",
    permissionAreas: ["account", "project", "content", "knowledge"] as const,
    actions: [
      "account.manage",
      "project.create",
      "project.manage",
      "file.workspace.use",
      "file.permission.manage",
      "document.tag.manage",
      "collection.manage",
      "view.manage",
      "processing.request",
      "knowledge.query",
      "knowledge.manage"
    ] as const
  })
} as const satisfies Readonly<Record<ProductRole, ProductRoleDefinition>>);

export const RoleAssignmentSchema = Type.Object(
  {
    schemaVersion: Type.Literal(PRODUCT_AUTHORIZATION_SCHEMA_VERSION),
    roleAssignmentId: UuidSchema,
    principalId: UuidSchema,
    role: GlobalProductRoleSchema,
    assignedByPrincipalId: UuidSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type RoleAssignment = Static<typeof RoleAssignmentSchema>;

export const GlobalRoleAssignmentPutInputSchema = Type.Object(
  { role: GlobalProductRoleSchema },
  { additionalProperties: false }
);
export type GlobalRoleAssignmentPutInput = Static<typeof GlobalRoleAssignmentPutInputSchema>;

export const RoleAssignmentListSchema = Type.Object(
  { items: Type.Array(RoleAssignmentSchema, { maxItems: 1 }) },
  { additionalProperties: false }
);
export type RoleAssignmentList = Static<typeof RoleAssignmentListSchema>;

export const ProductRoleCatalogSchema = Type.Object(
  {
    roles: Type.Array(
      Type.Object(
        {
          role: ProductRoleSchema,
          target: Type.Literal("global"),
          permissionAreas: Type.Array(PermissionAreaSchema, { uniqueItems: true }),
          actions: Type.Array(ProductActionSchema, { uniqueItems: true })
        },
        { additionalProperties: false }
      ),
      { minItems: 2, maxItems: 2 }
    )
  },
  { additionalProperties: false }
);
export type ProductRoleCatalog = Static<typeof ProductRoleCatalogSchema>;

export function productRoleCatalog(): ProductRoleCatalog {
  return {
    roles: Object.values(PRODUCT_ROLE_CATALOG).map((definition) => ({
      role: definition.role,
      target: definition.target,
      permissionAreas: [...definition.permissionAreas],
      actions: [...definition.actions]
    }))
  };
}

export function productActionsForAssignments(
  assignments: readonly RoleAssignment[]
): ProductAction[] {
  return [
    ...new Set(
      assignments.flatMap((assignment) => [...PRODUCT_ROLE_CATALOG[assignment.role].actions])
    )
  ].sort();
}

export function productActionsForHuman(assignments: readonly RoleAssignment[]): ProductAction[] {
  return [...new Set([...AUTHENTICATED_HUMAN_ACTIONS, ...productActionsForAssignments(assignments)])]
    .sort();
}

export const ProductSessionSchema = Type.Object(
  {
    principal: Type.Object(
      {
        principalId: UuidSchema,
        principalType: Type.Literal("human"),
        serviceId: Type.Null()
      },
      { additionalProperties: false }
    ),
    account: Type.Object(
      {
        email: Type.String({ format: "email", maxLength: 320 }),
        displayName: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()])
      },
      { additionalProperties: false }
    ),
    externalBindings: Type.Array(ExternalAccountBindingSchema, { maxItems: 2 }),
    roleAssignments: Type.Array(RoleAssignmentSchema, { maxItems: 1 }),
    allowedActions: Type.Array(ProductActionSchema, { uniqueItems: true })
  },
  { additionalProperties: false }
);
export type ProductSession = Static<typeof ProductSessionSchema>;
